/**
 * #40 · Entrega del archivo de datos variables al impresor.
 *
 * `GET /label-orders/{id}/print-file` devuelve una **URL firmada temporal** al
 * objeto cifrado. Tres cosas que esta pantalla tiene que dejar clarísimas, y
 * por las que existe como panel y no como un botón "descargar":
 *
 * 1. **La clave de descifrado no está acá y no viaja por ninguna API.** Se le
 *    entrega al proveedor por un canal fuera de banda (§4.1 de la
 *    especificación al proveedor). Una URL firmada interceptada no da acceso a
 *    un solo secreto — pero sólo si nadie manda la clave por el mismo canal
 *    que el enlace, y eso lo decide la persona que está en esta pantalla.
 * 2. **El enlace vence.** Se muestra en cuántos minutos, para que nadie lo
 *    pegue en un ticket creyendo que va a servir mañana.
 * 3. **El SHA-256 es lo que el impresor tiene que verificar** antes de
 *    imprimir. Va monoespaciado y seleccionable de una sola pasada.
 *
 * La URL se pide **a demanda** (`enabled` recién cuando alguien la reclama) y
 * con `staleTime: 0`: cachear un enlace que vence sería dárselo a alguien ya
 * muerto y hacerle creer que el bucket falla.
 */
import { useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import { labelOrderKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState, LoadingState } from '@/ui/states';
import { DetailList, DetailRow } from '../products/ui/fields';

type PrintFile = Schemas['PrintFileOut'];

/** Vigencia del enlace, en segundos. La API acepta de 60 a 86.400. */
const EXPIRES_IN = 900;

export function PrintFilePanel({ orderId }: { orderId: number }): ReactNode {
  const { t, fmt } = useI18n();
  const [requested, setRequested] = useState(false);

  const printFile = useApiQuery<PrintFile>(
    labelOrderKeys.printFile(orderId),
    `/api/v1/label-orders/${String(orderId)}/print-file`,
    { expires_in: EXPIRES_IN },
    { enabled: requested, staleTime: 0 },
  );

  if (!requested) {
    return (
      <div className="space-y-2">
        <Button
          onClick={() => {
            setRequested(true);
          }}
        >
          {t('labels.printFile.request')}
        </Button>
        <p className="text-xs text-ink-subtle">{t('labels.printFile.request.hint')}</p>
      </div>
    );
  }

  if (printFile.isPending) return <LoadingState rows={3} />;
  if (printFile.isError) {
    return (
      <ErrorState
        error={printFile.error}
        onRetry={() => {
          void printFile.refetch();
        }}
      />
    );
  }

  const data = printFile.data;

  return (
    <div className="space-y-3">
      <div
        role="note"
        className="rounded-(--radius-card) border border-suspect/30 bg-suspect-soft p-4"
      >
        <p className="text-sm font-semibold text-suspect">{t('labels.printFile.key.title')}</p>
        <p className="mt-1 text-sm text-ink">{t('labels.printFile.key.detail')}</p>
      </div>

      <DetailList>
        <DetailRow label={t('labels.printFile.records')} value={fmt.number(data.records)} />
        <DetailRow label={t('labels.printFile.bytes')} value={fmt.number(data.bytes)} />
        <DetailRow
          label={t('labels.printFile.expires')}
          value={t('labels.printFile.expires.value', { minutes: Math.round(data.expires_in / 60) })}
        />
        <DetailRow label={t('labels.printFile.cipher')} value={<code className="font-mono">{data.cipher}</code>} />
      </DetailList>

      <div>
        <p className="text-xs tracking-wide text-ink-subtle uppercase">
          {t('labels.printFile.sha256')}
        </p>
        <code className="mt-0.5 block font-mono text-xs break-all text-ink select-all">
          {data.sha256}
        </code>
      </div>

      <div>
        <p className="text-xs tracking-wide text-ink-subtle uppercase">
          {t('labels.printFile.nonce')}
        </p>
        <code className="mt-0.5 block font-mono text-xs break-all text-ink select-all">
          {data.nonce}
        </code>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <a
          href={data.url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex h-10 items-center rounded bg-brand px-4 text-sm font-medium text-brand-contrast hover:bg-brand-hover focus:ring-2 focus:ring-accent/40 focus:outline-none"
        >
          {t('labels.printFile.download')}
        </a>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void printFile.refetch();
          }}
        >
          {t('labels.printFile.renew')}
        </Button>
      </div>
    </div>
  );
}
